/**
 * The TanStack Query client.
 *
 * ===========================================================================
 * RETRY IS OFF FOR REFUSALS, AND THAT IS A SECURITY-ADJACENT SETTING
 * ===========================================================================
 * The default (retry three times) is wrong for this API. A 401 has already been
 * handled once inside `apiRequest` — refreshed and retried — so a query-level
 * retry after that means the SECOND attempt with a fresh token also failed, i.e. a
 * real refusal. Retrying it would present credentials repeatedly against an
 * endpoint that has said no, which against Phase 0's revoke-on-rotate refresh and
 * its login lockout counter is exactly the wrong instinct.
 *
 * 403 `COUNTRY_SCOPE_VIOLATION` and 404 are likewise permanent: a CD-scoped pilot
 * who lands on an IN record will never be allowed in, and three more attempts only
 * make three more audit rows.
 *
 * Transport failures and 5xx ARE retried — twice — because those are the ones that
 * genuinely succeed on a second try.
 */

import { QueryClient } from '@tanstack/react-query';
import { ApiRequestError, ApiTransportError } from './api';

/** Statuses that will never become a 200 by asking again. */
const PERMANENT = new Set([400, 401, 403, 404, 409, 422, 423]);

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (error instanceof ApiRequestError) {
            return !PERMANENT.has(error.status) && failureCount < 2;
          }
          if (error instanceof ApiTransportError) return failureCount < 2;
          return false;
        },
        // The pipeline is a shared workspace: a coordinator switching tabs should
        // see what a colleague just changed, but not re-fetch on every focus tick.
        staleTime: 30_000,
        refetchOnWindowFocus: true,
      },
      mutations: {
        // A mutation is not idempotent. Never retried automatically — a duplicated
        // stage PATCH would put a second row in the audit trail for one human act.
        retry: false,
      },
    },
  });
}
